import type { LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

import { sectionTitleClassName, subsectionTitleClassName } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

type SectionProps = Omit<ComponentProps<"section">, "title"> & {
  title: ReactNode;
  icon?: LucideIcon;
  /** Header actions. Use `size="sm"` buttons (`icon-sm` when icon-only). */
  actions?: ReactNode;
  headingLevel?: 2 | 3;
};

/**
 * Panel-free content section: a heading row plus content, without card chrome.
 * Use for operational detail pages and tabs. Group siblings in `SectionStack`
 * so they are separated by a top rule instead of nested borders.
 */
export function Section({
  title,
  icon: Icon,
  actions,
  headingLevel = 2,
  className,
  children,
  ...props
}: SectionProps) {
  const Heading = headingLevel === 2 ? "h2" : "h3";

  return (
    <section className={cn("flex min-w-0 flex-col gap-3 text-xs/relaxed", className)} {...props}>
      <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
        <Heading
          className={cn(
            "flex items-center gap-2",
            headingLevel === 2 ? sectionTitleClassName : subsectionTitleClassName,
          )}
        >
          {Icon ? <Icon aria-hidden="true" className="text-muted-foreground size-4" /> : null}
          {title}
        </Heading>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Vertical stack of sections separated by a hairline rule. */
export function SectionStack({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex min-w-0 flex-col gap-6 [&>*+*]:border-t [&>*+*]:pt-6", className)}
      {...props}
    />
  );
}
